import { useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Alert, Button, Stack } from '@mui/material';
import type { CreateCampaignFormValue } from '@features/Admin/Campaign/model/type';
import { campaignRequestMapper } from '@features/Admin/Campaign/utils/campaign-mapper';
import { PhoneNumberList } from '@pages/Admin/Main/PhoneNumbers/PhoneNumberList';
import {
  useCampaignUpdateControllerCampaignUpdateMutation,
  type CampaignDto,
  type CustomerRequestDto,
} from '@shared/services/apiService';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';

export const AudienceWorkspace = ({
  campaign,
  locked,
  onDirtyChange,
}: {
  campaign: CampaignDto;
  locked: boolean;
  onDirtyChange: (value: boolean) => void;
}) => {
  const form = useForm<CreateCampaignFormValue>({
    defaultValues: { id: campaign.id, title: campaign.title, customers: [] },
  });
  const [save, { isLoading }] = useCampaignUpdateControllerCampaignUpdateMutation();
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const changed = useRef(false);
  const saving = useRef(false);
  const change = (value: boolean) => {
    changed.current = value;
    setDirty(value);
    onDirtyChange(value);
  };
  const apply = async () => {
    if (locked || !changed.current || saving.current) return;
    saving.current = true;
    try {
      const values = form.getValues();
      const prepared: CustomerRequestDto[] | undefined = values.customers?.map((customer) => ({
        ...customer,
        gender:
          customer.gender === 'male' || customer.gender === 'female' ? customer.gender : undefined,
      }));
      const { customers } = campaignRequestMapper(values, prepared);
      await save({ id: campaign.id, campaignUpdateRequestDto: { customers } }).unwrap();
      change(false);
      setError('');
      setRefresh((n) => n + 1);
    } catch (failure) {
      setError(getApiErrorMessage(failure, 'Unable to save audience.'));
    } finally {
      saving.current = false;
    }
  };
  return (
    <FormProvider {...form}>
      <Stack gap={2}>
        {locked && (
          <Alert severity="info">
            Audience changes are locked for your current role or campaign state.
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" gap={1}>
          <Button
            variant="contained"
            disabled={locked || !dirty || isLoading}
            onClick={() => void apply()}>
            Save audience
          </Button>
          <Button
            disabled={!dirty || isLoading}
            onClick={() => {
              form.reset();
              change(false);
              setRefresh((n) => n + 1);
            }}>
            Discard changes
          </Button>
        </Stack>
        <PhoneNumberList
          key={refresh}
          campaignId={campaign.id}
          setCustomersListChanged={change}
          responseLoadingFlag={isLoading}
          isCampaignActive={locked}
          refreshKey={refresh}
          customersTotal={campaign.customersTotal}
        />
      </Stack>
    </FormProvider>
  );
};
